import NiceModal from '@ebay/nice-modal-react';
import { MatrixClient, Room } from 'matrix-js-sdk/src/matrix';

import { MeetingResponse } from 'api/meedioBackend/types';
import { updateMeeting } from 'api/meedioBackend/updateMeeting';
import logger from 'utils/logger';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import retryPromise from 'utils/retryPromise';

import { findConflictingGroupChats } from './findConflictingGroupChats';
import GroupChatConflictModal from './GroupChatConflictModal';
import { postLinkMessengerRoom } from './postLinkMessengerRoom';

export interface LinkMeetingGroupChatParams {
  matrixClient: MatrixClient;
  matrixRoomId: string;
  meeting: MeetingResponse;
  meetingTitle: string;
  participantMxids: string[];
  groupChatEnabled: boolean;
  messengerRoomId?: string;
  workspaceId?: string | null;
  /** When true, sends MESSENGER_CALL state + MEETING_STARTED to the messenger room so the sidebar and chat timeline reflect the active instant call. */
  isInstant?: boolean;
  /** Name for a newly-created Messenger group room (e.g. "{user}'s chat"). */
  groupChatName?: string;
  /**
   * Bound creator from `useMessengerRoomCreation` (the shared core), already closing over the
   * workspace context (workspaceId + meeting-scope managers/access/power-levels). Injected by the
   * React boundary (`InstantCallModal`) so this stays a pure, hook-free async function.
   */
  createMessengerRoom: (args: { name?: string; participantMxids: string[] }) => Promise<{
    roomId: string;
    failedInvites: string[];
  }>;
}

const retryOpts = {
  retries: 3,
  delayMs: (n: number) => 500 * 2 ** n,
  shouldRetry: () => true,
};

/**
 * For instant calls only: after `postLinkMessengerRoom`, also writes the two events the
 * messenger sidebar and timeline need to surface the active call:
 *  - `MESSENGER_CALL` state on the messenger room  → sidebar reads call_room_id from here
 *  - `MEETING_STARTED` timeline event on the messenger room → chat shows "started a meeting"
 */
const postInstantMessengerEvents = async (
  matrixClient: MatrixClient,
  matrixRoomId: string,
  messengerRoomId: string,
  meetingTitle: string
): Promise<void> => {
  const now = new Date().toISOString();
  const userId = matrixClient.getUserId();

  try {
    await matrixClient.sendStateEvent(messengerRoomId, CustomMeedioMatrixEvents.MESSENGER_CALL, {
      call_room_id: matrixRoomId,
      started_at: now,
      started_by: userId,
    });
  } catch (error) {
    logger.warn('linkMeetingGroupChat: MESSENGER_CALL state failed', { error });
  }

  try {
    await matrixClient.sendEvent(messengerRoomId, CustomMeedioMatrixEvents.MEETING_STARTED, {
      meeting_room_id: matrixRoomId,
      started_at: now,
      started_by: userId,
      is_instant: true,
      title: meetingTitle,
    });
  } catch (error) {
    logger.warn('linkMeetingGroupChat: MEETING_STARTED event failed', { error });
  }
};

/**
 * Links a meeting space to a Messenger group chat — same behaviour as schedule-meeting submit.
 * When `messengerRoomId` is set, links directly. When `groupChatEnabled`, runs conflict detection / create / pick flow.
 * Pass `isInstant: true` for instant calls so the messenger sidebar + timeline show the active call.
 */
export const linkMeetingGroupChat = async ({
  matrixClient,
  matrixRoomId,
  meeting,
  meetingTitle,
  participantMxids,
  groupChatEnabled,
  messengerRoomId,
  workspaceId,
  isInstant = false,
  groupChatName,
  createMessengerRoom,
}: LinkMeetingGroupChatParams): Promise<void> => {
  if (messengerRoomId) {
    await postLinkMessengerRoom(matrixClient, matrixRoomId, messengerRoomId, meeting, meetingTitle);
    if (isInstant) {
      await postInstantMessengerEvents(matrixClient, matrixRoomId, messengerRoomId, meetingTitle);
    }
    return;
  }

  if (!groupChatEnabled) return;

  let conflicts: Room[];
  try {
    conflicts = findConflictingGroupChats(matrixClient, participantMxids, workspaceId);
  } catch (error) {
    logger.warn('linkMeetingGroupChat: conflict check failed, skipping', { error });
    conflicts = [];
  }

  if (conflicts.length === 0) {
    try {
      const newRoom = await retryPromise({
        fn: () => createMessengerRoom({ name: groupChatName, participantMxids }),
        ...retryOpts,
        label: 'createMessengerRoom',
      });
      await retryPromise({
        fn: () => updateMeeting(meeting.id, { messenger_room_id: newRoom.roomId }),
        ...retryOpts,
        label: 'updateMeeting-groupChat',
      });
      await postLinkMessengerRoom(matrixClient, matrixRoomId, newRoom.roomId, meeting, meetingTitle);
      if (isInstant) {
        await postInstantMessengerEvents(matrixClient, matrixRoomId, newRoom.roomId, meetingTitle);
      }
    } catch (error) {
      logger.warn('linkMeetingGroupChat: auto-create messenger room failed', { error });
    }
    return;
  }

  const conflictResult = (await NiceModal.show(GroupChatConflictModal, {
    meetingId: meeting.id,
    conflicts,
    participantMxids,
  })) as { action: string; roomId: string } | undefined;

  if (conflictResult?.roomId) {
    await postLinkMessengerRoom(matrixClient, matrixRoomId, conflictResult.roomId, meeting, meetingTitle);
    if (isInstant) {
      await postInstantMessengerEvents(matrixClient, matrixRoomId, conflictResult.roomId, meetingTitle);
    }
  }
};
